import React, { forwardRef, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Button from './Button';
import Tooltip from './Tooltip';
import { cn } from './cn';

/** Proportional colored segments with rounded outer ends and full-height targets. */
const StackedBar = forwardRef(function StackedBar({ segments, interactive = true, onSelect, onHover, activeSegmentId, renderTooltip, className, 'aria-label': ariaLabel }, ref) {
  const segmentRefs = useRef(new Map());
  const [hoveredId, setHoveredId] = useState(null);
  const [anchorElement, setAnchorElement] = useState(null);
  const anchorRef = useMemo(() => ({ current: anchorElement }), [anchorElement]);
  const tooltipId = useId();
  const activeId = activeSegmentId === undefined ? hoveredId : activeSegmentId;
  const activeSegment = segments.find((segment) => segment.id === activeId);
  const inspect = (segment) => {
    setHoveredId(segment?.id ?? null);
    onHover?.(segment);
  };
  useLayoutEffect(() => {
    const anchor = segmentRefs.current.get(activeId);
    setAnchorElement(anchor?.getClientRects().length ? anchor : null);
  }, [activeId, segments]);
  const tooltipOpen = !!(renderTooltip && activeSegment && anchorElement);
  if (!interactive) {
    const visibleSegments = segments.filter(segment => Number.isFinite(segment.value) && segment.value > 0);
    return <div ref={ref} role="img" aria-label={ariaLabel} className={cn('flex h-2 overflow-hidden rounded-full bg-bun-mist-deep', className)}>
      {visibleSegments.map(segment => <span key={segment.id} aria-hidden="true" className="basis-0 border-r border-bun-surface last:border-r-0" style={{ flexGrow: segment.value, backgroundColor: segment.color }} />)}
    </div>;
  }
  return (
    <div ref={ref} role="group" aria-label={ariaLabel} className={cn('flex h-11 items-center', className)}>
      {segments.map((segment, index) => (
        <Button
          key={segment.id}
          ref={(element) => {
            if (element) segmentRefs.current.set(segment.id, element);
            else segmentRefs.current.delete(segment.id);
          }}
          data-segment-id={segment.id}
          variant="ghost"
          aria-label={segment.label}
          aria-describedby={tooltipOpen && segment.id === activeId ? tooltipId : undefined}
          aria-disabled={!onSelect}
          onClick={() => onSelect?.(segment)}
          onMouseEnter={() => inspect(segment)}
          onMouseLeave={() => inspect(null)}
          onFocus={() => inspect(segment)}
          onBlur={() => inspect(null)}
          className={cn('h-11 min-w-[3px] basis-0 p-0 hover:bg-transparent hover:opacity-80', !onSelect && 'cursor-default hover:opacity-100')}
          style={{ flexGrow: segment.value }}
        >
          <span
            aria-hidden="true"
            style={{ backgroundColor: segment.color }}
            className={cn(
              'h-4 w-full border-r border-bun-surface',
              index === 0 && 'rounded-l-full',
              index === segments.length - 1 && 'rounded-r-full border-r-0'
            )}
          />
        </Button>
      ))}
      {tooltipOpen && (
        <Tooltip portal multiline variant="surface" align="center" width="max-content"
          id={tooltipId} anchorRef={anchorRef} open onOpenChange={(nextOpen) => { if (!nextOpen) inspect(null); }}
          label={renderTooltip(activeSegment)} />
      )}
    </div>
  );
});

export default StackedBar;
